import { useContext, useEffect, useState } from 'react';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import Tooltip from '../common/Tooltip';
import { SavedIcon } from '../../StateManagment/SavedIcon';
import copyElementAsImage, { hasNothingToCopy } from '../../helpers/export/copyElementAsImage';

const LABEL = 'Kopiëren als afbeelding';
const COPIED = 'Gekopieerd. Plak de afbeelding waar je hem nodig hebt.';
/** Zo lang staat het vinkje bij de knop. */
const COPIED_FOR_MS = 3000;

/**
 * De reden in gewone taal. De browser geeft Engelse meldingen ('Document is not
 * focused.'); die horen in de console, niet in een Nederlandse melding. Onze
 * eigen fouten uit copyElementAsImage zijn al Nederlands.
 *
 * @param {Error & {name?: string}} e
 * @returns {string}
 */
const copyErrorReason = (e) => {
    if (e?.name === 'NotAllowedError') return 'de browser gaf geen toestemming om naar het klembord te schrijven';
    if (e?.name === 'NotFocusedError' || /not focused/i.test(e?.message || '')) return 'het venster was niet actief; klik eerst in de pagina en probeer het opnieuw';
    if (e?.name && e.name !== 'Error') return 'de browser weigerde de afbeelding';
    return e?.message || 'onbekende fout';
};

/**
 * 'Kopiëren als afbeelding' in de onderbalk van een kaart: de inhoud (titel,
 * grafiek en de cijfertabel eronder) als plaatje naar het klembord, om in
 * PowerPoint of een mail te plakken. Updateverzoek Groentjessoep, 2026-10-26.
 *
 * Eén knop in het kaartomhulsel (DashboardCard, AnalysisCardFrame) in plaats
 * van per grafiek: zo heeft elke kaart hem, ook een kaart die later bijkomt.
 *
 * @param {object} props
 * @param {React.RefObject<HTMLElement>} props.targetRef - De kaartinhoud die gekopieerd wordt
 * @returns {JSX.Element}
 */
const CopyCardButton = ({ targetRef }) => {
    const [, , , setSavedMessage] = useContext(SavedIcon);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
        return () => clearTimeout(timer);
    }, [copied]);

    const copyHandler = async () => {
        if (busy || !targetRef.current) return;
        if (hasNothingToCopy(targetRef.current)) {
            setSavedMessage('Deze kaart heeft nog geen gegevens, dus er is niets gekopieerd.');
            return;
        }
        setBusy(true);
        try {
            await copyElementAsImage(targetRef.current);
            // De bevestiging staat bij de knop, niet in de melding onderin het
            // scherm: die lag seconden lang over het kopieer-icoon van de
            // onderste kaarten (testronde 2026-10-28).
            setCopied(true);
        } catch (e) {
            // Zichtbaar, en met de reden: 'mag niet' en 'kan niet in deze
            // browser' vragen allebei iets anders van de gebruiker.
            console.error('Kaart kopiëren mislukt:', e);
            setSavedMessage(`Kopiëren is niet gelukt: ${copyErrorReason(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        /* Een echte knop met een naam: de tooltip is een extraatje.
           AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile
           maxWidth, geen vaste `top`: de tekst past niet in de breedte van het
           icoon en loopt over twee regels, en met een vaste afstand naar boven
           lag de tooltip dan óver het icoon. Met maxWidth meet de tooltip zijn
           eigen hoogte en gaat hij onder of boven het icoon staan. */
        <Tooltip content={copied ? COPIED : LABEL} maxWidth='200px'>
            <button
                type='button'
                className={`dashboard-card-options-icon${copied ? ' dashboard-card-options-icon--copied' : ''}`}
                aria-label={LABEL}
                onClick={copyHandler}
                disabled={busy}
            >
                {copied ? <CheckIcon /> : <ContentCopyOutlinedIcon />}
            </button>
            {/* Voor een schermlezer; het vinkje is wat je ziet. */}
            <span role='status' className='copy-card-status'>{copied ? COPIED : ''}</span>
        </Tooltip>
    );
};

export default CopyCardButton;
